// Opposing-counsel detail page.
//
// GET /api/opposing-counsel/:id returns the counsel record plus the matters
// they're linked to (either side of the two-way pointer) in the viewer's
// scope (the firm for admin+). Hero shows name/firm/role/contact; the
// matter list below is clickable rows to /matter/:eID.

import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchOpposingCounselDetail } from "../../api/mock";
import { practiceAreaById, practiceAreaLabel } from "../../data/practice-areas";
import { caseKindLabel, stageLabel } from "../../lib/caseLabels";
import { recordStep, usePageTrail } from "../chrome/trail";
import { EntityLink } from "../chrome/EntityLink";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { SEP } from "../chrome/SepList";
import { EmptyCopy } from "../chrome/EmptyCopy";

export function OpposingCounselDetail() {
  const navigate = useNavigate();
  const { counselId } = useParams<{ counselId: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ["opposing-counsel", counselId ?? ""],
    queryFn: () => (counselId ? fetchOpposingCounselDetail(counselId) : Promise.resolve(null)),
    enabled: !!counselId,
  });
  usePageTrail([
    { label: "Opposing counsel" },
    recordStep(data?.counsel?.name, isLoading, "Counsel"),
  ]);

  if (isLoading) {
    return (
      <div className="main">
        <div style={{ padding: 18 }}>
          <SkeletonGroup label="Loading opposing counsel">
            <Skeleton width="35%" height={20} />
            <Skeleton width="55%" height={12} />
            <div style={{ height: 8 }} />
            <Skeleton width="100%" height={120} radius={10} />
          </SkeletonGroup>
        </div>
      </div>
    );
  }
  if (!data || !data.counsel) {
    return (
      <div className="main">
        <div className="detail-body">
          <div className="cc-card" style={{ padding: 18 }}>
            <p style={{ color: "var(--muted)", fontSize: 12 }}>
              Opposing counsel <code>{counselId}</code> not found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const oc = data.counsel;
  const matters = data.matters;

  return (
    <div className="main">
      <div className="detail-body">
        <div className="cc-card" style={{ padding: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)" }}>{oc.name}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
            {[oc.role, oc.firm].filter(Boolean).join(" · ") || "—"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              marginTop: 8,
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {oc.email ? (
              <a className="cc-link" href={`mailto:${oc.email}`}>
                {oc.email}
              </a>
            ) : null}
            {oc.phone ? (
              <a className="cc-link" href={`tel:${oc.phone}`}>
                {oc.phone}
              </a>
            ) : null}
          </div>
        </div>

        <div className="cc-card" style={{ marginTop: 14 }}>
          <div className="cc-card-head">
            <div className="cc-card-title">Matters opposite this counsel</div>
            <div className="cc-card-sub">· {matters.length}</div>
          </div>
          <div>
            {matters.length === 0 ? (
              <div className="cc-empty">
                <div className="icon" aria-hidden>
                  ⌗
                </div>
                <EmptyCopy
                  title="No matters opposite this counsel yet"
                  mine="No matters assigned to you are opposite this counsel"
                  body="Matters linked to this counsel appear here, closed ones included."
                />
              </div>
            ) : (
              matters.map((m) => {
                const pa = practiceAreaById(m.practiceArea);
                const kind = caseKindLabel(m);
                return (
                  <div
                    key={m.eID}
                    className="row clickable cc-row-lift"
                    onClick={() => navigate(`/matter/${m.eID}`)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: 14,
                      padding: "12px 18px",
                      borderTop: "1px solid var(--line-2)",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <span className="pa-dot lg" style={{ background: pa?.accent }} />
                    <div>
                      <div className="row-title">
                        {pa ? (
                          <EntityLink kind="practiceArea" id={pa.id} stopPropagation subtle>
                            {pa.label}
                          </EntityLink>
                        ) : (
                          practiceAreaLabel(m.practiceArea)
                        )}
                        {kind ? SEP + kind : ""}
                      </div>
                      <div className="row-sub">{stageLabel(m.stage)}</div>
                    </div>
                    <EntityLink
                      kind="matter"
                      id={m.eID}
                      eID={m.eID}
                      stopPropagation
                      subtle
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--ink-2)",
                      }}
                    >
                      {m.eID}
                    </EntityLink>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="cc-detail-footer">
          {oc.updatedAt ? (
            <>
              <span>
                Last updated{SEP}
                {oc.updatedAt.slice(0, 10)}
              </span>
              <span className="sep">{SEP.trim()}</span>
            </>
          ) : null}
          <span>
            {matters.length} linked matter{matters.length === 1 ? "" : "s"}
          </span>
          {oc.firm ? (
            <>
              <span className="sep">·</span>
              <span>{oc.firm}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
